import React, { useState, useEffect } from 'react';
import { Users, ArrowRight, ArrowLeft, RotateCcw, Copy, CheckCircle, User, HelpCircle, MapPin, Printer, Quote, Flame, Feather, Home } from 'lucide-react';

export default function ThreeAdvisors({ onBack }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [maxStepReached, setMaxStepReached] = useState(0);
  
  const [data, setData] = useState({
    topic: '',
    goldenQuestion: '',
    meetingPlace: '',
    advisorName1: '',
    advisorName2: '',
    advisorName3: '',
    advisor1Response: '',
    advisor2Response: '',
    advisor3Response: '',
    synthesis: '',
    actionStep: ''
  });
  const [isCopied, setIsCopied] = useState(false);

  const getAdvisorName = (num) => {
    const name = data[`advisorName${num}`];
    return name && name.trim() !== '' ? name : `Rádce č. ${num}`;
  };

  const steps = [
    { id: 'intro', shortTitle: 'Úvod', title: 'Tři rádcové - profesionální nástroj pro kouče by Mind Spark', description: 'Získejte odpovědi skrze moudrost tří postav.', icon: <Flame size={32} />, bgColor: 'bg-[#ff8474]', textColor: 'text-white' },
    { id: 'topic', shortTitle: 'Téma', title: 'Vaše Téma', description: 'Co je tou situací, kterou dnes chcete prozkoumat?', placeholder: 'Zde si poznamenejte své téma...', inputType: 'textarea', field: 'topic', bgColor: 'bg-[#FAF6F2]', textColor: 'text-[#2C2C2C]' },
    { id: 'goldenQuestion', shortTitle: 'Otázka', title: 'Klíčová otázka', description: 'Existuje jedna otázka, na kterou kdybyste znali odpověď, vyřešilo by to vaši situaci.', placeholder: 'Jak zní ta otázka?', inputType: 'textarea', field: 'goldenQuestion', icon: <HelpCircle size={28} />, bgColor: 'bg-[#FFEDD5]', textColor: 'text-[#7C2D12]' },
    { id: 'setupAdvisors', shortTitle: 'Výběr', title: 'Pozvání rádců', description: 'Zvolte si tři postavy (reálné, fiktivní nebo archetypální).', inputType: 'custom_advisors', bgColor: 'bg-white', textColor: 'text-[#2C2C2C]', icon: <Users size={28} /> },
    { id: 'meetingPlace', shortTitle: 'Místo', title: 'Místo setkání', description: 'Kde se s vašimi rádci potkáte? Popište atmosféru tohoto prostoru.', placeholder: 'Popis místa...', inputType: 'textarea', field: 'meetingPlace', icon: <MapPin size={28} />, bgColor: 'bg-[#F5F5F4]', textColor: 'text-[#44403C]' },
    { id: 'advisor1', shortTitle: '1. Rádce', title: `Setkání: ${getAdvisorName(1)}`, description: `Vstupujete do prostoru "${data.meetingPlace}". Čeká tu ${getAdvisorName(1)}.`, placeholder: `Odpověď rádce...`, inputType: 'textarea', field: 'advisor1Response', icon: <User size={28} />, bgColor: 'bg-[#FFF7ED]', textColor: 'text-[#7C2D12]' },
    { id: 'advisor2', shortTitle: '2. Rádce', title: `Setkání: ${getAdvisorName(2)}`, description: `Nyní přichází ${getAdvisorName(2)}. Jaká je odpověď z této perspektivy?`, placeholder: `Odpověď rádce...`, inputType: 'textarea', field: 'advisor2Response', icon: <User size={28} />, bgColor: 'bg-[#FAFAF9]', textColor: 'text-[#57534E]' },
    { id: 'advisor3', shortTitle: '3. Rádce', title: `Setkání: ${getAdvisorName(3)}`, description: `Jako poslední přichází ${getAdvisorName(3)}. Co vám radí?`, placeholder: `Odpověď rádce...`, inputType: 'textarea', field: 'advisor3Response', icon: <User size={28} />, bgColor: 'bg-[#FED7AA]', textColor: 'text-[#7C2D12]' },
    { id: 'synthesis', shortTitle: 'Syntéza', title: 'Porada Rádců', description: 'V čem se shodují? V čem se liší? Jaké nové řešení vyplývá?', placeholder: 'Můj vhled...', inputType: 'textarea', field: 'synthesis', bgColor: 'bg-white', textColor: 'text-[#2C2C2C]' },
    { id: 'action', shortTitle: 'Akce', title: 'První krok', description: 'Jaký je jeden konkrétní krok, který uděláte?', placeholder: 'Můj závazek...', inputType: 'textarea', field: 'actionStep', icon: <CheckCircle size={28} />, bgColor: 'bg-[#e06b5c]', textColor: 'text-white' },
    { id: 'summary', shortTitle: 'Shrnutí', title: 'Shrnutí Sezení', description: 'Přehled vaší cesty.', bgColor: 'bg-[#FAF6F2]', textColor: 'text-[#2C2C2C]' }
  ];

  useEffect(() => {
    if (currentStep > maxStepReached) setMaxStepReached(currentStep);
  }, [currentStep, maxStepReached]);

  const handleNext = () => { if (currentStep < steps.length - 1) setCurrentStep(currentStep + 1); };
  const handlePrev = () => { if (currentStep > 0) setCurrentStep(currentStep - 1); };
  const jumpToStep = (index) => { if (index <= maxStepReached) setCurrentStep(index); };
  const handleChange = (e) => { const { name, value } = e.target; setData({ ...data, [name]: value }); };
  const handlePrint = () => { window.print(); };

  const copyToClipboard = () => {
    const text = `Téma: ${data.topic}\nKlíčová otázka: ${data.goldenQuestion}\nMísto setkání: ${data.meetingPlace}\n-------------------\n1. ${getAdvisorName(1)}: ${data.advisor1Response}\n2. ${getAdvisorName(2)}: ${data.advisor2Response}\n3. ${getAdvisorName(3)}: ${data.advisor3Response}\n-------------------\nSyntéza: ${data.synthesis}\nAkční krok: ${data.actionStep}`;
    navigator.clipboard.writeText(text);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const resetApp = () => {
    if(window.confirm("Opravdu chcete vymazat všechna data a začít znovu?")) {
      setData({ topic: '', goldenQuestion: '', meetingPlace: '', advisorName1: '', advisorName2: '', advisorName3: '', advisor1Response: '', advisor2Response: '', advisor3Response: '', synthesis: '', actionStep: '' });
      setCurrentStep(0);
      setMaxStepReached(0);
    }
  };

  const currentStepData = steps[currentStep];

  const StepIndicator = () => (
    <div className="flex justify-center mb-6 px-4 print:hidden">
      <div className="flex items-center gap-1.5 bg-white p-2 rounded-full shadow-sm border border-[#e5ddd2]">
        {steps.map((step, index) => {
          const isCompleted = index < currentStep;
          const isActive = index === currentStep;
          const isClickable = index <= maxStepReached;
          return (
            <button key={step.id} onClick={() => jumpToStep(index)} disabled={!isClickable}
              className={`relative rounded-full transition-all duration-300 flex items-center justify-center
                ${isActive ? 'w-8 h-8 bg-[#ff8474] text-white shadow-md' : 'w-2 h-2'}
                ${isCompleted && !isActive ? 'bg-[#e5ddd2] w-2 h-2 hover:bg-[#ff8474]' : ''}
                ${!isActive && !isCompleted ? 'bg-[#e5ddd2]' : ''}
                ${isClickable ? 'cursor-pointer' : 'cursor-default'}`}
              title={step.shortTitle}
            >
              {isActive && <span className="text-[10px] font-bold">{index + 1}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderContextCards = () => {
    if (!currentStepData.id.startsWith('advisor')) return null;
    return (
      <div className="flex flex-col gap-3 mb-8 text-sm">
        <div className="flex items-start gap-3 p-3 rounded-xl bg-[#F5F5F4] border border-[#E7E5E4] text-[#44403C]">
           <MapPin size={16} className="mt-0.5 shrink-0 text-[#A8A29E]" /> 
           <div>
             <span className="text-xs font-bold uppercase tracking-wider text-[#A8A29E] block mb-1">Místo setkání</span>
             <span className="italic">"{data.meetingPlace || "..."}"</span>
           </div>
        </div>
        <div className="flex items-start gap-3 p-3 rounded-xl bg-[#FFF7ED] border border-[#FFEDD5] text-[#9A3412]">
           <HelpCircle size={16} className="mt-0.5 shrink-0 text-[#ff8474]" /> 
           <div>
             <span className="text-xs font-bold uppercase tracking-wider text-[#ff8474] block mb-1">Otázka</span>
             <span className="italic">"{data.goldenQuestion || "..."}"</span>
           </div>
        </div>
      </div>
    );
  };

  // Summary View
  if (currentStepData.id === 'summary') {
    return (
      <div className="min-h-screen bg-[#FAF6F2] p-4 md:p-8 print:bg-white print:p-0" style={{ fontFamily: 'Montserrat, sans-serif' }}>
        <div className="max-w-4xl mx-auto print:max-w-none">
          <div className="flex justify-between items-center mb-8 print:hidden">
            <div className="flex items-center gap-4">
              {onBack && (
                <button
                  onClick={onBack}
                  className="p-2 text-[#a69d90] hover:text-[#ff8474] hover:bg-white rounded-lg transition-colors"
                  title="Zpět na dashboard"
                >
                  <Home size={20} />
                </button>
              )}
              <h1 className="text-2xl font-bold text-[#2C2C2C] flex items-center gap-2">
                <Quote size={24} className="text-[#ff8474]"/> Váš zápis
              </h1>
            </div>
            <div className="flex gap-2">
              <button onClick={resetApp} className="p-2 text-[#a69d90] hover:text-[#ff8474] rounded-lg hover:bg-white transition-colors" title="Nové sezení"><RotateCcw size={18} /></button>
              <button onClick={copyToClipboard} className="p-2 text-[#a69d90] hover:text-[#ff8474] rounded-lg hover:bg-white transition-colors" title="Kopírovat">{isCopied ? <CheckCircle size={18} className="text-green-500"/> : <Copy size={18} />}</button>
              <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 bg-[#ff8474] text-white rounded-lg font-bold hover:bg-[#e06b5c] transition-colors text-sm"><Printer size={16} /> Tisk</button>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-[#e5ddd2] print:shadow-none print:border-none">
            <div className="bg-[#2C2C2C] text-white p-8 print:bg-white print:text-black print:border-b-2 print:border-[#e5ddd2]">
               <div className="flex justify-between items-start">
                 <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-[#ff8474] mb-2">Tři rádcové</p>
                    <h2 className="text-2xl font-bold mb-1 print:text-[#2C2C2C]">{data.topic || "Téma sezení"}</h2>
                    <p className="text-sm text-[#A8A29E] print:text-[#57534E]">{new Date().toLocaleDateString('cs-CZ')}</p>
                 </div>
                 <div className="text-right bg-white/10 p-4 rounded-xl print:bg-[#FAF6F2]">
                    <p className="text-[9px] font-bold uppercase tracking-widest text-[#ff8474] mb-1">Klíčová otázka</p>
                    <p className="text-sm font-medium max-w-xs text-right print:text-[#2C2C2C]">"{data.goldenQuestion || "..."}"</p>
                 </div>
               </div>
            </div>

            <div className="p-8 space-y-8 print:p-0">
               <div className="grid lg:grid-cols-3 gap-6 print:block print:space-y-6">
                 {[1, 2, 3].map(num => (
                   <div key={num} className="bg-[#FAFAF9] p-5 rounded-lg border border-[#E7E5E4] print:bg-white print:border print:border-[#e5ddd2] print:mb-4">
                     <h3 className="text-sm font-bold text-[#2C2C2C] mb-3 flex items-center gap-2 uppercase tracking-wide">
                       <span className="w-5 h-5 rounded-full bg-[#FFEDD5] flex items-center justify-center text-[10px] text-[#e06b5c]">{num}</span>
                       {getAdvisorName(num)}
                     </h3>
                     <p className="text-[#57534E] text-sm leading-relaxed whitespace-pre-wrap">{data[`advisor${num}Response`] || "..."}</p>
                   </div>
                 ))}
               </div>

               <div className="grid md:grid-cols-2 gap-6 print:block print:space-y-6">
                  <div className="bg-white p-6 rounded-xl border border-[#E7E5E4] shadow-sm print:border-[#e5ddd2] print:shadow-none print:mb-4">
                     <h3 className="text-[#2C2C2C] text-sm font-bold uppercase tracking-wide mb-3 flex items-center gap-2">
                       <Users size={14} className="text-[#ff8474]"/> Syntéza
                     </h3>
                     <p className="text-[#57534E] italic text-sm leading-relaxed">{data.synthesis || "..."}</p>
                  </div>
                  <div className="bg-[#ff8474] text-white p-6 rounded-xl shadow-lg print:bg-white print:text-black print:border print:border-[#ff8474] print:shadow-none">
                     <h3 className="text-[#FFEDD5] text-xs font-bold uppercase tracking-widest mb-3 flex items-center gap-2 print:text-[#ff8474]">
                        <CheckCircle size={14} className="text-white print:text-[#ff8474]"/> První krok
                     </h3>
                     <p className="text-lg font-bold print:text-black">{data.actionStep || "..."}</p>
                  </div>
               </div>
            </div>

            <div className="bg-[#FAFAF9] p-4 border-t border-[#E7E5E4] flex justify-center print:hidden">
              <button onClick={handlePrev} className="text-[#A8A29E] hover:text-[#57534E] font-medium text-xs flex items-center gap-2 transition-colors">
                <ArrowLeft size={12} /> Zpět k úpravám
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Wizard View
  return (
    <div className="min-h-screen bg-[#FAF6F2] flex flex-col items-center justify-center p-4 transition-colors duration-700" style={{ fontFamily: 'Montserrat, sans-serif' }}>
      
      {/* Home button */}
      {onBack && (
        <div className="fixed top-4 left-4 z-50 print:hidden">
          <button
            onClick={onBack}
            className="p-2 bg-white text-[#a69d90] hover:text-[#ff8474] rounded-lg shadow-sm border border-[#e5ddd2] transition-colors"
            title="Zpět na dashboard"
          >
            <Home size={20} />
          </button>
        </div>
      )}

      <div className="w-full max-w-xl relative">
        <StepIndicator />

        <div className="bg-white shadow-xl shadow-[#ff8474]/10 rounded-3xl overflow-hidden border border-white">
          
          <div className={`relative p-8 text-center ${currentStepData.bgColor} ${currentStepData.textColor} transition-colors duration-500 ease-in-out`}>
             {currentStepData.icon && (
               <div className={`relative z-10 mx-auto w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${currentStepData.textColor === 'text-white' ? 'bg-white/20' : 'bg-white shadow-sm'}`}>
                 <div className={currentStepData.textColor === 'text-white' ? 'text-white' : 'text-[#ff8474]'}>
                    {currentStepData.icon}
                 </div>
               </div>
             )}
             
             <h1 className="relative z-10 text-2xl font-bold mb-2">{currentStepData.title}</h1>
             <p className="relative z-10 text-sm font-medium max-w-sm mx-auto leading-relaxed opacity-90">{currentStepData.description}</p>
          </div>

          <div className="p-6 md:p-8 bg-white">
            {currentStepData.id === 'intro' ? (
               <div className="text-center">
                 <button onClick={handleNext} className="inline-flex items-center justify-center px-8 py-3 font-bold text-white transition-all bg-[#ff8474] rounded-full hover:bg-[#e06b5c] hover:-translate-y-0.5 shadow-lg shadow-[#ff8474]/20 text-sm tracking-wide">
                   Začít <ArrowRight size={16} className="ml-2" />
                 </button>
               </div>
            ) : (
              <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                
                {renderContextCards()}

                {currentStepData.inputType === 'custom_advisors' ? (
                  <div className="space-y-4">
                    {[1, 2, 3].map(num => (
                      <div key={num} className="group">
                        <label className="block text-[10px] font-bold text-[#A8A29E] uppercase tracking-widest mb-1.5 ml-1">{num}. Rádce</label>
                        <input type="text" name={`advisorName${num}`} value={data[`advisorName${num}`]} onChange={handleChange}
                          placeholder={num === 1 ? "Moudrý stařec..." : num === 2 ? "Vnitřní dítě..." : "Kritik..."}
                          className="w-full px-4 py-3 bg-[#FAF6F2] border border-[#E7E5E4] focus:border-[#ff8474] focus:bg-white outline-none transition-all rounded-lg text-base text-[#2C2C2C] placeholder:text-[#D6D3D1]"
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="relative">
                    <div className="absolute top-4 left-4 text-[#D6D3D1]"><Feather size={16} /></div>
                    <textarea name={currentStepData.field} value={data[currentStepData.field]} onChange={handleChange} placeholder={currentStepData.placeholder}
                      className="w-full h-48 pl-10 pr-4 py-4 bg-[#FAF6F2] rounded-xl border border-[#E7E5E4] focus:bg-white focus:border-[#ff8474]/50 outline-none transition-all resize-none text-base text-[#2C2C2C] placeholder:text-[#D6D3D1] leading-relaxed font-medium"
                      autoFocus
                    />
                  </div>
                )}
                
                <div className="flex justify-between items-center mt-6 pt-6 border-t border-[#F5F5F4]">
                  <button onClick={handlePrev} className="text-[#A8A29E] hover:text-[#57534E] px-2 py-2 font-medium flex items-center gap-1.5 transition-colors text-xs uppercase tracking-wider">
                    <ArrowLeft size={14} /> Zpět
                  </button>
                  
                  <button onClick={handleNext}
                    disabled={currentStepData.inputType === 'custom_advisors' ? (!data.advisorName1 || !data.advisorName2 || !data.advisorName3) : !data[currentStepData.field]}
                    className={`px-6 py-2.5 rounded-full font-bold transition-all flex items-center gap-2 text-sm shadow-md
                      ${(currentStepData.inputType === 'custom_advisors' ? (!data.advisorName1 || !data.advisorName2 || !data.advisorName3) : !data[currentStepData.field])
                        ? 'bg-[#F5F5F4] text-[#D6D3D1] shadow-none cursor-not-allowed' 
                        : 'bg-[#ff8474] text-white hover:bg-[#e06b5c] hover:-translate-y-0.5'}`}
                  >
                    {currentStep === steps.length - 2 ? 'Dokončit' : 'Pokračovat'} <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
